import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TilbyDatePipe } from '@tilby/tilby-ui-lib/pipes/tilby-date';
import { CustomFormControl, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { restManager, util } from 'app/ajs-upgraded-providers';
import { debounceTime, filter, map, merge, Observable } from 'rxjs';
import {
  StockLocationOption,
  StockMovementsFilterFields,
  StockMovementsFilterForm,
  StockMovementsFilterFormGroup,
  stockMovementTypeOptions
} from '../models/stock-movements.model';
import { StockMovementsFiltersState, StockMovementsStateService } from './stock-movements-state.service';

// ultimo id dei magazzini di sistema, il cui nome va tradotto
const SYSTEM_STOCK_LOCATIONS_MAX_ID = 100;
const DEFAULT_STOCK_LOCATION_ID = 1;
const DATE_DEBOUNCE_MS = 1000;

@Injectable()
export class StockMovementsFilterService {

  private readonly restManagerService = inject(restManager);
  private readonly translateService = inject(TranslateService);
  private readonly utilService = inject(util);
  private readonly stateService = inject(StockMovementsStateService);
  private readonly tilbyDatePipe = inject(TilbyDatePipe);

  private filterForm!: StockMovementsFilterFormGroup;

  get form() {
    return this.filterForm;
  }

  readonly movementTypes = stockMovementTypeOptions;
  readonly stockLocations = signal<StockLocationOption[]>([]);

  readonly searchText = signal(this.stateService.getState().searchText ?? '');
  readonly barcode = signal('');

  private getDefaultFilters(): StockMovementsFiltersState {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    return {
      date_from: yesterday,
      date_to: today,
      stock_location_id: DEFAULT_STOCK_LOCATION_ID,
      movement_type: 'allManuals'
    };
  }

  getSummary(): string {
    const { date_from, date_to, stock_location_id, movement_type } = this.filterForm.getRawValue();

    const typeLabel = this.movementTypes.find(type => type.value === movement_type)?.label;
    const locationName = this.stockLocations().find(location => location.id === stock_location_id)?.name;
    const formatDate = (date: Date | null) => date ? this.tilbyDatePipe.transform(date, 'dd/MM/yyyy') : '';

    const dates = [formatDate(date_from), formatDate(date_to)].filter(Boolean).join(' - ');

    return [typeLabel ? this.translateService.instant(typeLabel) : '', locationName, dates]
      .filter(Boolean)
      .join(' · ');
  }

  createForm() {
    const defaults = this.getDefaultFilters();
    const saved = this.stateService.getState().filters;

    this.filterForm = new CustomFormGroup<StockMovementsFilterForm>({
      date_from: new CustomFormControl<StockMovementsFilterFields['date_from']>(
        saved ? saved.date_from : defaults.date_from,
        // { validators: [Validators.required] }
      ),
      date_to: new CustomFormControl<StockMovementsFilterFields['date_to']>(
        saved ? saved.date_to : defaults.date_to,
        // { validators: [Validators.required] }
      ),
      stock_location_id: new CustomFormControl<StockMovementsFilterFields['stock_location_id']>(
        saved ? saved.stock_location_id : defaults.stock_location_id
      ),
      movement_type: new CustomFormControl<StockMovementsFilterFields['movement_type']>(
        saved ? saved.movement_type : defaults.movement_type
      )
    });

    this.filterForm.valueChanges.subscribe(() => {
      if (this.filterForm.valid) {
        this.stateService.updateState({ filters: this.filterForm.getRawValue() as StockMovementsFiltersState });
      }
    });

    return this.filterForm;
  }

  get filtersChanged$(): Observable<void> {
    const dates$ = merge(this.date_from!.valueChanges, this.date_to!.valueChanges)
      .pipe(debounceTime(DATE_DEBOUNCE_MS));
    const selects$ = merge(this.stock_location_id!.valueChanges, this.movement_type!.valueChanges);

    return merge(dates$, selects$).pipe(
      filter(() => this.filterForm.valid),
      map(() => undefined)
    );
  }

  async loadStockLocations() {
    const locations: StockLocationOption[] = await this.restManagerService.getList('stock_locations');

    this.stockLocations.set(locations.map(location => ({
      ...location,
      name: location.id <= SYSTEM_STOCK_LOCATIONS_MAX_ID
        ? this.translateService.instant(`STOCK.LOCATIONS.${location.name}`)
        : location.name
    })));
  }

  buildQuery(): Record<string, any> {
    const { date_from, date_to, stock_location_id, movement_type } = this.filterForm.getRawValue();
    const query: Record<string, any> = {};

    if (this.barcode()) {
      query['barcode'] = this.barcode();
    }

    if (date_from) {
      const since = new Date(date_from);
      since.setHours(0, 0, 0, 0);
      query['date_since'] = since.toISOString();
    }

    if (date_to) {
      const max = new Date(date_to);
      max.setHours(23, 59, 59, 999);
      query['date_max'] = max.toISOString();
    }

    if (stock_location_id != null) {
      query['stock_location_id'] = stock_location_id;
    }

    switch (movement_type) {
      case 'allManuals':
        query['sale_id'] = 'null';
        break;
      case 'load':
      case 'unload':
      case 'setup':
        query['type'] = movement_type;
        break;
      case 'unloadSales':
        query['type'] = 'unload';
        query['sale_id'] = 'notnull';
        break;
      case 'unloadManual':
        query['type'] = 'unload';
        query['sale_id'] = 'null';
        break;
      default:
        break;
    }

    return query;
  }

  // filtri + ricerca testuale, per la lista piatta di /stock_movements
  buildSearchQuery(): Record<string, any> {
    const query = this.buildQuery();
    const text = this.searchText();

    if (text) {
      query['x_query'] = this.buildSearchXQuery(text);
    }

    return query;
  }

  private buildSearchXQuery(text: string): string {
    const encoded = this.utilService.fixedEncodeURIComponent(text);

    const conditions = [
      `item_id=${encoded}`,
      `code=${encoded}`,
      `name_like=${encoded}`,
      `supplier_name_like=${encoded}`,
      `item.sku_like=${encoded}`,
      `item.department.name_like=${encoded}`,
      `item.category.name_like=${encoded}`,
      `supplier_order_like=${encoded}`,
    ];

    return `OR(${conditions.join(',')})`;
  }

  get date_from() {
    return this.filterForm.controls.date_from;
  }

  get date_to() {
    return this.filterForm.controls.date_to;
  }

  get stock_location_id() {
    return this.filterForm.controls.stock_location_id;
  }

  get movement_type() {
    return this.filterForm.controls.movement_type;
  }
}
